import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';
import { TableTemplate } from '../../../components/ui/tableTemplate/TableTemplate';
import { TablePaginationBar } from '../../../components/ui/TablePaginationBar';
import { useTableState } from '../../../hooks/useTableState';
import { useGetTerrains } from '../../instances/hooks/useGetTerrains';
import type { LabeledCode } from '../../../lib/labeledCode';
import { useGetPayslips } from '../hooks/useGetPayslips';
import { usePayslipColumns } from '../hooks/usePayslipColumns';
import { PAYSLIP_SHOW_IDS } from '../config/payslipList.constants';
import type { Payslip } from '../payslipTypes';

interface PayslipsTableProps {
  /** Employee whose payslips are listed. */
  employeeId: string;
  /** Row click opens the payslip detail (detail view delivered in a later ticket). */
  onOpenDetail?: (payslip: Payslip) => void;
}

/**
 * Read-only payslips (bulletins) table — reuses the shared {@link TableTemplate}
 * (same look as the employee table). Column definitions live in
 * `hooks/usePayslipColumns.tsx`; this component only wires data, table state, and
 * row interaction.
 *
 * Data comes from the payslips API via {@link useGetPayslips}; sort/filter/page
 * state is held by the shared {@link useTableState} hook and sent server-side
 * (all columns are sortable). On API failure an error message is shown instead
 * of any rows — no mock/demo fallback.
 */
export function PayslipsTable({ employeeId, onOpenDetail }: PayslipsTableProps) {
  const { t } = useTranslation('payments');
  const {
    sortState,
    handleSortChange,
    filters,
    debouncedFilters,
    setFilter,
    clearFilter,
    page,
    limit,
    setPage,
    setLimit,
  } = useTableState<Payslip>();

  const { data: terrains } = useGetTerrains();
  const terrainOptions = useMemo<LabeledCode[]>(
    () => terrains.map(terrain => ({ code: terrain.codeterrain, libelle: terrain.libelle })),
    [terrains],
  );

  // Hidden (not removed): the ID columns are defined in usePayslipColumns and gated
  // by this flag. See PAYSLIP_SHOW_IDS in config/payslipList.constants.ts.
  const showIds = PAYSLIP_SHOW_IDS;
  const columns = usePayslipColumns({ showIds, terrainOptions });

  const { data, loading, error, meta } = useGetPayslips(
    employeeId,
    columns,
    sortState,
    debouncedFilters,
    page,
    limit,
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* On API failure the list is empty and we show an explicit error — never
          demo/sample data that could be mistaken for real payslips. */}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {t('payslips.table.loadError')}
        </Alert>
      )}
      <TableTemplate<Payslip>
        columns={columns}
        rows={data}
        striped
        loading={loading}
        fillHeight
        stickyHeader
        toolbarActions={
          <Typography variant="body2" color="text.label">
            <b>{meta?.total ?? 0}</b> {t('payslips.table.count', { count: meta?.total ?? 0 })}
          </Typography>
        }
        emptyStateMessage={t('payslips.table.empty')}
        // No "try adjusting your filters" hint: the payslip list empty state stands
        // on its own ("Aucun bulletin…" / "No payslip…") — see HOMCO payslips spec.
        showEmptyStateHint={false}
        sortState={sortState}
        onSortChange={handleSortChange}
        filterState={filters}
        onFilterChange={setFilter}
        onFilterClear={clearFilter}
        onRowClick={row => onOpenDetail?.(row)}
        pagination={
          meta ? (
            <TablePaginationBar
              meta={meta}
              page={page}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          ) : undefined
        }
      />
    </Box>
  );
}
